import { useEffect, useState } from "react";
import AnimationPage from "../../shared/animation-page/AnimationPage";
import { useParams, Link as RouterLink } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Link,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import GBottomSaveBar from "../../design/components/GBottomSaveBar";
import { JService } from "../../api-service/ApiService";
import "./Developer.css";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EyeIcon } from "../../../assets/eye.svg";
import { ReactComponent as EyeCloseIcon } from "../../../assets/eyeoff.svg";
import { ReactComponent as CopyIcon } from "../../../assets/copy.svg";
import DeleteAppModal from "./DeleteAppModal";
import WebHookDetails from "./WebHookDetails";


const BASE_URL = process.env.REACT_APP_BETA;
const APP_URL = `${BASE_URL}/api/v2/oauth/apps/`;
const WEB_URL = `${BASE_URL}/api/v2/integrations/webhooks/`;


const isRedirectUrlValid = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 1000) return false;
  const regex = new RegExp(
    "^(https?:\\/\\/)" +
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
      "localhost|" +
      "\\d{1,3}(\\.\\d{1,3}){3})" +
      "(:\\d+)?" +
      "(\\/[-a-z\\d%@_.~+&:]*)*" +
      "(\\?[;&a-z\\d%@_.,~+&:=-]*)?" +
      "(\\#[-a-z\\d_]*)?$$",
    "i"
  );
  return regex.test(trimmed);
};

// centralized sx
const sx = {
  backLink: {
    color: "success.main",
    textDecoration: "none",
    fontWeight: 500,
    lineHeight: "20px",
    fontSize: "14px",
    "&:hover": {
      color: "success.main",
      textDecoration: "none",
    },
  },
  headerBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  title: {
    fontWeight: "500",
    fontSize: "20px",
    lineHeight: "30px",
    p: 1,
    marginTop: "4px",
    marginBottom: "4px",
  },
  menuButton: {
    marginBlock: "1rem",
    marginRight: "2rem",
    width: "28px",
    height: "28px",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "1px",
    transform: "rotate(90deg)",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  textField: {
    width: "100%",
    maxWidth: 420,
    marginTop: "0.5rem",
    marginBottom: "0.5rem",
    "& .MuiOutlinedInput-root": {
      height: 36,

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#34cb65",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "6px 10px",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "20px",
      color: "#3F3F46FF",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#34cb65",
    },
  },
  copyBox: {
    display: "flex",
    alignItems: "center",
    borderLeft: "1px solid #ccc",
    padding: "3px 6px",
    height: 36,
    lineHeight: "20px",
    fontSize: "20px",
    fontWeight: "500",
    color: "#3F3F46FF",
    cursor: "pointer",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
};

const AppDetailsPage = () => {
  const [appLoading, setAppLoading] = useState(true);
  const [hookLoading, setHookLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();
  const [encoded, setEncoded] = useState("");
  const [app, setApp] = useState({});
  const [initialApp, setInitialApp] = useState(null);
  const [appName, setAppName] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [webHooks, setWebHooks] = useState([]);
  const [createWebhook, setCreateWebhook] = useState(false);
  const [flag,setFlag] = useState(0);

  const handleOpenWebhookModal = () => setCreateWebhook(true);
  const handleCloseWebhookModal = () => setCreateWebhook(false);

  const handleInputChange = (field, value) =>
    setApp((prev) => ({ ...prev, [field]: value }));

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleDeleteButton = () => {
    handleMenuClose();
    setShowDeleteModal(true);
  };

  const isChanged =
    app &&
    initialApp &&
    (app.name !== initialApp.name ||
      app.redirect_uris !== initialApp.redirect_uris);
  const redirectUriValid = app ? app.redirect_uris?.trim() !== '' : true;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).catch((err) => console.error(err));
  };

  const toggleSecretVisibility = () => setShowSecret((v) => !v);

  const handleDelete = () => {
    JService.delete(`${APP_URL}${id}/`)
      .then(() => {
        enqueueSnackbar("App deleted successfully.", { variant: "success" });
        navigate("/settings/?tab=Developer");
      })
      .catch((error)=>(console.error(error)));
  };

  const handleSave = () => {
    if (!redirectUriValid) return;
    const payload = {
      id: app.id,
      name: app.name,
      redirect_uris: app.redirect_uris,
    };
    JService.patch(`${APP_URL}${id}/`, payload)
      .then(() => {
        enqueueSnackbar("App updated successfully.", { variant: "success" });
        setAppName(app.name);
        setInitialApp(app);
      })
      .catch((error)=>{
        console.error(error);
        enqueueSnackbar(error?.message || "Failed to save changes", {variant:'error'})
      });
  };

  useEffect(() => {
    if (!id) return;
    setAppLoading(true);
    JService.get(`${APP_URL}${id}/`)
      .then((res) => {
        const currentApp = res.apps[0];
        setApp(currentApp);
        setAppName(currentApp.name);
        setInitialApp(currentApp);
      })
      .catch((error) => {
        console.error("Error fetching app:", error);
      })
      .finally(() => {
      setAppLoading(false);
    });
  }, [id]);

  // GET ALL WEBHOOKS FOR APP
  useEffect(() => {
    setHookLoading(true);
    if (!app?.client_id || !app?.client_secret) return;
  const encoded_id =btoa(`${app.client_id}:${app.client_secret}`);
  setEncoded(encoded_id);
    JService.get(
      `${WEB_URL}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${encoded_id}`,
        },
      }
    )
      .then((res) => {
        console.log("Success :", res);
        setWebHooks(res.data);
      })
      .catch((error) => {
        console.error("Error fetching app:", error);
      })
      .finally(() => {
      setHookLoading(false); 
    });
  }, [app?.client_id, flag]);
  if (!app) return <Typography>App not found</Typography>;

  return (
    <Box className="app-box">
      { appLoading ? 
      <AnimationPage variant="transparent" height="180px" />
      :
      <>
      <Box className="element">
        <Link
          component={RouterLink}
          to="/settings?tab=Developer"
          underline="none"
          sx={sx.backLink}
        >
          &lt; Back to developer
        </Link>

        <Box sx={sx.headerBox}>
          <Typography variant="h6" sx={sx.title}>
            {appName}
          </Typography>

          <IconButton size="small" onClick={handleMenuOpen} sx={sx.menuButton}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={handleDeleteButton}>Delete</MenuItem>
          </Menu>
        </Box>
      </Box>

      <Divider />

      <Box mt={2} sx={{ p: 1 }} className="element">
        <Typography
          mb={4}
          variant="h6"
          sx={{ fontSize: "18px", lineHeight: "28px", fontWeight: "500" }}
        >
          App details
        </Typography>

        <Box className="app-flex-box">
          <Box className="app-input">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                lineHeight: "20px",
                color: "#3F3F46",
                fontSize: "14px",
              }}
            >
              App name
            </Typography>
            <TextField
              size="small"
              value={app.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              sx={sx.textField}
            />
          </Box>

          <Box className="app-input">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                lineHeight: "20px",
                color: "#3F3F46",
                fontSize: "14px",
              }}
            >
              Redirect URL
            </Typography>
            <TextField
              size="small"
              value={app.redirect_uris}
              onChange={(e) =>
                handleInputChange("redirect_uris", e.target.value)
              }
              error={!redirectUriValid}
              helperText={!redirectUriValid ? "Invalid redirect URL" : ""}
              sx={sx.textField}
            />
          </Box>

          <Box className="app-input" sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                lineHeight: "20px",
                color: "#3F3F46",
                fontSize: "14px",
              }}
            >
              Client id
            </Typography>
            <TextField
              size="small"
              value={app.client_id}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{
                      mr: -1.5,
                      display: "flex",
                      alignItems: "center",
                      marginLeft: "auto",
                    }}
                  >
                    <Box
                      onClick={() => handleCopy(app.client_id)}
                      sx={sx.copyBox}
                    >
                      {/* <ContentCopy sx={{ fontSize: 16, mr: 0.5, ml: 0.5 }} /> */}
                      <CopyIcon
                        width={20}
                        height={20}
                        stroke="#000"
                        strokeWidth={5}
                        fill="none"
                        style={{ margin: "0px 4px" }}
                      />
                      <Typography
                        sx={{
                          lineHeight: "28px",
                          letterSpacing: "0",
                          fontWeight: "500",
                          fontSize: "14px",
                          color: "#000",
                        }}
                      >
                        Copy
                      </Typography>
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                ...sx.textField,
                "& .MuiOutlinedInput-input[readonly]": {
                  color: "black",
                  fontWeight: 400,
                },
              }}
            />
          </Box>

          <Box className="app-input" sx={{ mb: 4 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                lineHeight: "20px",
                color: "#3F3F46",
                fontSize: "14px",
              }}
            >
              Client secret
            </Typography>
            <TextField
              size="small"
              type={showSecret ? "text" : "password"}
              value={app.client_secret}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ height: "100%", mr: -1.5 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "stretch" }}>
                      <IconButton
                        onClick={toggleSecretVisibility}
                        size="small"
                        sx={{ p: "4px" }}
                      >
                        {showSecret ? (
                          <EyeCloseIcon width={20} height={20} />
                        ) : (
                          <EyeIcon width={20} height={20} />
                        )}
                      </IconButton>
                      <Box
                        onClick={() => handleCopy(app.client_secret)}
                        sx={sx.copyBox}
                      >
                        {/* <ContentCopy sx={{ fontSize: 16, mr: 0.5, ml: 0.5 }} /> */}
                        <CopyIcon
                          width={20}
                          height={20}
                          stroke="#000"
                          strokeWidth={5}
                          fill="none"
                          style={{ margin: "0px 4px" }}
                        />
                        <Typography
                          sx={{
                            lineHeight: "28px",
                            letterSpacing: "0",
                            fontWeight: "500",
                            fontSize: "14px",
                            color: "#000",
                          }}
                        >
                          Copy
                        </Typography>
                      </Box>
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                ...sx.textField,
                "& .MuiOutlinedInput-input": {
                  padding: "6px 10px",
                  fontWeight: 500,
                  color: "black",
                },
                "& .MuiOutlinedInput-input[readonly]": {
                  color: "black",
                  fontWeight: 400,
                },
              }}
            />
          </Box>
        </Box>
      </Box>
      </>
}
      <Divider />

      <WebHookDetails
        countWebhook={webHooks.length}
        webhooks={webHooks}
        createWebhook={createWebhook}
        handleOpen={handleOpenWebhookModal}
        handleClose={handleCloseWebhookModal}
        isRedirectUrlValid={isRedirectUrlValid}
        flag = {flag}
        setFlag = {setFlag}
        encoded= {encoded}
        hookloading = {hookLoading}
      />

      <GBottomSaveBar
        show={isChanged}
        onCancel={() => navigate("/settings?tab=Developer")}
        onSave={handleSave}
        firstBtnText="Cancel"
        secondBtnText="Save changes"
        firstBtnDisabled={false}
        secondBtnDisabled={false}
        secondBtnLoading={false}
      />
      <DeleteAppModal
        visible={showDeleteModal}
        closeModal={() => setShowDeleteModal(false)}
        onDelete={() => {
          handleDelete();
          setShowDeleteModal(false);
        }}
        appName={appName}
      />
    </Box>
  );
};

export default AppDetailsPage;
